import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { subYears } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TourPurchaseGuestsInfo,
  TourPurchaseNumberOfGuests,
} from '../pages/TourPurchase';
import { IndividualForm, IndividualFormValues } from './IndividualForm';
import { ExpandMoreTwoTone } from '@mui/icons-material';
import { useAccountWithGuard } from '../hooks/api/account-with-guard';

export function TourPurchaseGuestsInfoComponent(props: {
  tourEndDate: Date;
  guests: TourPurchaseNumberOfGuests;
  guestsInfo: TourPurchaseGuestsInfo;
  onIndividualSubmit: (
    data: IndividualFormValues,
    type: 'adults' | 'children',
    position: number,
  ) => Promise<void>;
}) {
  const [error, setError] = useState<string>();
  const [expanded, setExpanded] = useState<number | false>(0);
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();
  const emptyPassenger = t('tourPurchaseGuestsInfo.empty');

  // ensure user is logged in
  useAccountWithGuard();

  async function handleSubmit(
    data: IndividualFormValues,
    type: 'adults' | 'children',
    i: number,
  ) {
    try {
      setSubmitting(true);
      setError(undefined);
      await props.onIndividualSubmit(data, type, i);
      setExpanded(i + 1 + (type === 'children' ? props.guests.adults : 0));
      setTimeout(() => {
        window.scrollTo({
          top: document.getElementById(`acc-${i + 1}`)?.offsetTop,
          behavior: 'smooth',
        });
      }, 400);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Check if the guest is saved to allow expanding the accordion.
   */
  function isExpandable(acc: 'adult' | 'child', i: number) {
    if (acc === 'adult') {
      return i === 0 || props.guestsInfo.adults[i - 1]?.id !== undefined;
    } else {
      return (
        props.guestsInfo.adults.length === props.guests.adults &&
        (i === 0 || props.guestsInfo.children[i - 1]?.firstName !== undefined)
      );
    }
  }

  return (
    <Box>
      <Typography sx={{ mb: 2, color: 'text.secondary' }}>
        {t('tourPurchaseGuestsInfo.note')}
      </Typography>
      {Array.from({ length: props.guests.adults }).map((_, i) => (
        <Accordion
          key={`adult-${i}`}
          id={`acc-${i}`}
          expanded={expanded === i}
          onChange={() =>
            isExpandable('adult', i) && setExpanded(expanded === i ? false : i)
          }
        >
          <AccordionSummary
            expandIcon={isExpandable('adult', i) && <ExpandMoreTwoTone />}
          >
            {t('tourPurchaseGuestsInfo.nthIndividual', {
              n: i + 1,
              name: props.guestsInfo.adults[i]?.firstName ?? emptyPassenger,
            })}
          </AccordionSummary>
          <AccordionDetails>
            <IndividualForm
              submitting={submitting}
              initialValues={props.guestsInfo.adults[i]}
              onSubmit={(data) => handleSubmit(data, 'adults', i)}
            />
          </AccordionDetails>
        </Accordion>
      ))}
      {Array.from({ length: props.guests.children }).map((_, i) => (
        <Accordion
          key={`child-${i}`}
          id={`acc-${props.guests.adults + i}`}
          expanded={expanded === props.guests.adults + i}
          onChange={() =>
            isExpandable('child', i) &&
            setExpanded(
              expanded === props.guests.adults + i
                ? false
                : props.guests.adults + i,
            )
          }
        >
          <AccordionSummary
            expandIcon={isExpandable('child', i) && <ExpandMoreTwoTone />}
          >
            {t('tourPurchaseGuestsInfo.nthChild', {
              n: i + 1,
              name:
                props.guestsInfo.children[i]?.firstName ??
                t('tourPurchaseGuestsInfo.empty'),
            })}
          </AccordionSummary>
          <AccordionDetails>
            <IndividualForm
              submitting={submitting}
              key={`adult-${i}-${props.guestsInfo.children[i]?.id || ''}`}
              initialValues={props.guestsInfo.children[i]}
              birthDateMin={subYears(
                props.tourEndDate,
                props.guests.childrenInfo[i].age + 1,
              )}
              birthDateMax={subYears(
                props.tourEndDate,
                props.guests.childrenInfo[i].age,
              )}
              onSubmit={(data) => handleSubmit(data, 'children', i)}
            />
          </AccordionDetails>
        </Accordion>
      ))}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
